<template>
  <div>
    <b-alert :show="mode === 'add' && !isLoading" variant="info">
      <i class="fas fa-info-circle fa-lg mr-1" />
      <b v-html="alertMessage" />
    </b-alert>
    <b-form @submit.prevent="save" v-show="!isLoading">
      <b-row>
        <b-col cols="12" md="12" v-if="mode == 'add'">
          <b-form-group
            :label="`${$lang.azmoonLang.survey_term}: `"
            label-for="term"
            :label-cols="this.$config.form.labelCol"
          >
            <requiredComponent
              slot="label"
              :label="`${$lang.azmoonLang.survey_term}: `"
              :rules="$v.term_selected.$params"
            />
            <selectAsync
              :options="term_options"
              v-model="$v.term_selected.$model"
              label="caption"
              :asyncFind="getTerms"
              :loading="term_loading"
              :isValid="
                $v.term_selected.$dirty ? !$v.term_selected.$error : null
              "
            />
            <b-form-invalid-feedback
              :style="{
                display: $v.term_selected.$error ? 'block' : 'none',
              }"
              id="term_selected_feedback"
            >
              {{ $lang.error_required | replace($lang.azmoonLang.survey_term) }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" v-if="mode == 'edit'">
          {{ $lang.azmoonLang.survey_term }} :
          {{ term_selected && term_selected.caption }}
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="$lang.azmoonLang.caption"
            label-for="caption"
            :label-cols="$config.azmoon.form.labelCol"
          >
            <requiredComponent
              slot="label"
              :label="$lang.azmoonLang.caption"
              :rules="$v.caption.$params"
            />
            <b-form-input
              id="caption"
              type="text"
              autocomplete="off"
              :state="$v.caption.$dirty ? !$v.caption.$error : null"
              v-model="$v.caption.$model"
            ></b-form-input>
            <b-form-invalid-feedback id="caption-feedback"
              >{{
                $lang.azmoonLang.error_required
                  | replace($lang.azmoonLang.caption)
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group
            :label="$lang.azmoonLang.start_date"
            label-for="start_date"
            :label-cols="$config.azmoon.form.labelCol"
          >
            <requiredComponent
              slot="label"
              :label="$lang.azmoonLang.start_date"
              :rules="$v.start_date.$params"
            />
            <date-picker
              type="datetime"
              v-model="$v.start_date.$model"
              :state="$v.start_date.$dirty ? !$v.start_date.$error : null"
              format="YYYY-MM-DD HH:mm:ss"
              display-format="dddd jDD jMMMM jYYYY HH:mm"
              inputReq="true"
              :class="
                $v.start_date.$dirty
                  ? $v.start_date.$error
                    ? 'invalid-date'
                    : 'valid-date'
                  : null
              "
            ></date-picker>

            <b-form-invalid-feedback id="start_date-feedback"
              >{{
                $lang.azmoonLang.error_required
                  | replace($lang.azmoonLang.start_date)
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group
            :label="$lang.azmoonLang.end_date"
            label-for="end_date"
            :label-cols="$config.azmoon.form.labelCol"
          >
            <requiredComponent
              slot="label"
              :label="$lang.azmoonLang.end_date"
              :rules="$v.end_date.$params"
            />
            <date-picker
              type="datetime"
              format="YYYY-MM-DD HH:mm:ss"
              display-format="dddd jDD jMMMM jYYYY HH:mm"
              v-model="$v.end_date.$model"
              :state="$v.end_date.$dirty ? !$v.end_date.$error : null"
              inputReq="true"
              :class="
                $v.end_date.$dirty
                  ? $v.end_date.$error
                    ? 'invalid-date'
                    : 'valid-date'
                  : null
              "
            ></date-picker>

            <b-form-invalid-feedback id="end_date-feedback"
              >{{
                $lang.azmoonLang.error_required
                  | replace($lang.azmoonLang.end_date)
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col v-if="mode === 'edit'" xs="12" md="12">
          <b-form-group
            :label="$lang.azmoonLang.status"
            label-for="azmoon_execute_status"
            :label-cols="$config.azmoon.form.labelCol"
          >
            <requiredComponent
              slot="label"
              :label="$lang.azmoonLang.status"
              :rules="$v.azmoon_execute_status.$params"
            />
            <selectFind
              :options="azmoon_execute_options"
              v-model="$v.azmoon_execute_status.$model"
              label="text"
              :isValid="
                $v.azmoon_execute_status.$dirty
                  ? !$v.azmoon_execute_status.$error
                  : null
              "
            />

            <b-form-invalid-feedback
              id="azmoon_execute_status-feedback"
              :style="{
                display: $v.azmoon_execute_status.$error ? 'block' : 'none',
              }"
            >
              {{
                $lang.azmoonLang.error_required
                  | replace($lang.azmoonLang.address_kind)
              }}</b-form-invalid-feedback
            >
          </b-form-group>
        </b-col>
        <b-col cols="12" md="12">
          <TagComponent
            category_name="tag_azmoon"
            :multiple="true"
            :requiredLabel="tagInputRequired"
            :parentPermissions="permissions"
            :checkValidation="checkTagSelected"
            :parentValue="tags"
            @updateCheckValidation="updateCheckTagSelected"
            @getSelected="getSelected"
          />
        </b-col>
        <b-col cols="12">
          <div slot="footer" class="form-group form-btn">
            <b-button
              type="submit"
              v-if="
                (includes(pathOr(null, ['status', 'id'], item), [1, 2]) &&
                  !includes('view', permissions)) ||
                mode === 'add'
              "
              size="md"
              variant="success"
            >
              {{ $lang.azmoonLang.submitButton }}
              <i class="fa fa-check"></i>
            </b-button>
            <b-button
              v-if="mode === 'add'"
              size="md"
              variant="danger"
              class="ml-2"
              @click="handleBack(false)"
            >
              {{ $lang.azmoonLang.cancelButton }}
              <i class="fa fa-times"></i>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { renderEumOptions } from "@/shared/utils";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import { required } from "vuelidate/lib/validators";
import { pathOr, includes } from "ramda";
import TagComponent from "@/components/tag/tagComponent.vue";

export default {
  name: "azmoon_execute_form",
  validations: {
    start_date: { required },
    end_date: { required },
    caption: { required },
    term_selected: { required },
    azmoon_execute_status: {},
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "add",
    },
    item: {
      type: Object,
      default: null,
    },
    permissions: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    datePicker: VuePersianDatetimePicker,
    TagComponent,
  },
  data() {
    return {
      term_selected: null,
      term_options: [],
      term_loading: false,
      azmoon_execute_status: null,
      start_date: null,
      end_date: null,
      company_options: [],
      companyLoading: false,
      caption: null,
      company_name: null,
      tagInputRequired: false,
      checkTagSelected: false,
      tags: [],
    };
  },
  computed: {
    azmoon_execute_options() {
      return renderEumOptions("azmoon_execute_status");
    },
    alertMessage() {
      return `فقط دوره‌هایی نمایش داده می شود که وضعیت آن <span class='text-primary'>درحال اجرا</span> باشد و زمان شروع آن از زمان فعلی کمتر و زمان پایان بیشتر از زمان فعلی باشد.`;
    },
  },
  created() {
    this.fetchData();
  },

  methods: {
    pathOr,
    includes,
    async getTerms(query) {
      if (!query || query.length < 2) {
        this.term_options = [];
        return;
      }
      try {
        this.term_loading = true;
        const url = `${this.$config.api_url}${this.$config.profile.term_combo}?search_txt=${query}`;
        const response = await this.$httpService.get(url);
        this.term_options = pathOr([], ["data", "data"], response).map(
          (item) => {
            return {
              caption: `${pathOr("", ["id"], item)} - ${pathOr(
                "",
                ["caption"],
                item
              )}`,
              id: pathOr(null, ["id"], item),
            };
          }
        );
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.term_loading = false;
      }
    },
    handleBack(getItems = true) {
      this.$emit("handleBack", getItems);
    },
    save() {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }
      this.checkTagSelected = true;
      if (this.tagInputRequired && !this.tags.length) return;

      const editStatus = this.azmoon_execute_options.find(
        (item) => item.category_id === 87
      );

      let payload = {
        term_id: pathOr(null, ["id"], this.term_selected),
        start_date: this.start_date,
        end_date: this.end_date,
        caption: this.caption,
        tag_id: this.tags.map((item) => item.value),
        azmoon_execute_status:
          this.mode === "add"
            ? pathOr(null, ["value"], editStatus)
            : pathOr(null, ["value"], this.azmoon_execute_status),
      };

      this.$emit("submit", payload);
    },
    fetchData() {
      if (this.item && Object.keys(this.item).length) {
        this.company_name = this.item.company && this.item.company.name;
        this.term_selected = this.item.term;
        this.caption = this.item.caption;
        this.start_date = this.item.start_date.gregorian;
        this.end_date = this.item.end_date.gregorian;
        if (this.item.status) {
          this.azmoon_execute_status = this.azmoon_execute_options.find(
            (x) => x.value == this.item.status.id
          );
        }
        if (this.item.tags) {
          this.tags = pathOr(null, ["tags"], this.item).map((x) => {
            return {
              text: pathOr(null, ["caption"], x),
              value: pathOr(null, ["id"], x),
            };
          });
        }
      }
    },
    updateCheckTagSelected(data) {
      this.checkTagSelected = data;
    },
    getSelected(data) {
      this.tags = data;
    },
  },
};
</script>
